import React, { useState, useEffect, useMemo } from 'react';
import propTypes from 'prop-types';
import dynamic from 'next/dynamic';

import { NextSeo } from 'next-seo';
import reportAmplitudeEvent from '@helpers/analytics';
import { PageWrapper } from '@components/pagewrapper/pagewrapper';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { initNeoGatewayServer } from '@helpers/neogateway';
import getOfferPlan from '@anghami/neogateway/dist/endpoints/getOfferPlan';
import withAuth from '@components/auth';
import { handleArabicEncoding } from '@helpers/helper';
import { AmplitudeEvents } from '@enums/amplitude';
import NeoGateway from '@anghami/neogateway';
import MainLoader from '@components/main-loader';
import { useTranslation } from 'next-i18next';

const MasterCard = dynamic(async () => import('../../components/mastercard/Mastercard'));
const Offer = dynamic(async () => import('../../components/offer/Offer'));
const Shahid = dynamic(async () => import('../../components/shahid/index'));

enum OFFER_TYPES {
	mastercard = 'mastercard',
	shahid = 'shahid'
}

export const OfferPage = ({ query, API_RESPONSE }) => {
	const type = query?.type?.toString() || '';
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (isLoading && API_RESPONSE) {
			handleOfferResponse(API_RESPONSE);
		}
	}, [data]);

	const reportCampaignLoadEvent = (data, type) => {
		const eventDetails = {
			campaign: type,
			type: data.requires_login ? 'not logged in' : data.plan_id ? 'eligible' : 'non eligible',
			url: data.button_link
		};
		reportAmplitudeEvent(AmplitudeEvents.campaignPageLoaded, eventDetails);
	};

	const handleOfferResponse = (data) => {
		let response = { ...data };

		if (data?.error) {
			setError(data?.error?.message || t('oops_tryagain'));
		}

		if (data?.title) {
			response = {
				...data,
				title: data.title.replace('<b>', '<span class=\'flash\'>')
			};
		}

		if (data?.requires_login) {
			response = {
				...response,
				button_link: `https://www.anghami.com/login?redirecturl=${window.location.href}`
			};
		}

		if (data?.dominant_colors) {
			const dominantColors = {};
			Object.keys(data?.dominant_colors).forEach((elt) => {
				dominantColors[elt] = handleArabicEncoding(response?.dominant_colors[elt]);
			});
			response = {
				...response,
				dominant_colors: dominantColors
			};
		}

		setData(response);
		setIsLoading(false);
		reportCampaignLoadEvent(response, type.toString().toLowerCase());
	};

	const renderedMetas = useMemo(
		() => (
			<NextSeo
				title={API_RESPONSE?.web_metas?.title}
				description={API_RESPONSE?.web_metas?.description}
				canonical={`https://www.anghami.com/offer/${type}`}
				openGraph={{
					url: `https://www.anghami.com/offer/${type}`,
					title: `${API_RESPONSE?.web_metas?.title}`,
					description: `${API_RESPONSE?.web_metas?.description}`,
					images: [
						{
							url: `${API_RESPONSE?.web_metas?.image}`,
							alt: `${API_RESPONSE?.web_metas?.title}`
						}
					],
					site_name: 'Anghami'
				}}
				twitter={{
					handle: '@anghami',
					site: '@anghami',
					cardType: 'summary_large_image'
				}}
			/>
		),
		[
			API_RESPONSE?.web_metas?.title,
			API_RESPONSE?.web_metas?.description,
			API_RESPONSE?.web_metas?.image,
			type
		]
	);

	const renderedComponent = useMemo(() => {
		switch (type) {
			case OFFER_TYPES.mastercard: {
				return <MasterCard data={data} />;
			}

			case OFFER_TYPES.shahid: {
				return <Shahid data={data} />;
			}

			default: {
				return <Offer type={type} data={data} urlParams={query} />;
			}
		}
	}, [type, data, query]);

	return (
		<>
			{renderedMetas}
			{isLoading ? (
				<MainLoader customClass="full_height" />
			) : (
				<PageWrapper hideHeader={false} hideFooter={false}>
					{error ? (
						<div className="flexbox colls centered min_height_75">
							<div className="main_page_error">{error}</div>
						</div>
					) : (
						renderedComponent
					)}
				</PageWrapper>
			)}
		</>
	);
};

OfferPage.propTypes = {
	query: propTypes.any.isRequired,
	API_RESPONSE: propTypes.any.isRequired
};

const fetchData = async (gateway: NeoGateway, queryParams) => {
	try {
		const _type = queryParams?.type?.toString() || '';
		const response = await gateway.callEndpoint(getOfferPlan, {
			offer: _type,
			output: 'jsonhp'
		});
		return response.data;
	} catch (_) {
		return { error: _ };
	}
};

export async function getServerSideProps(ctx: any) {
	const { gateway, pageProps } = await initNeoGatewayServer(ctx);
	return {
		props: {
			...pageProps,
			...(await serverSideTranslations(ctx.locale, ['common'])),
			query: ctx.query,
			API_RESPONSE: await fetchData(gateway, ctx.query)
		}
	};
}

export default withAuth(OfferPage);
